import { createSlice, createSelector, createAction } from "@reduxjs/toolkit";
import { apiCallBegan } from "./api";

const slice = createSlice({
  name: "brokerage",
  initialState: {
    information: [],
    isUpdating: false,
    updateErrors: [],
    isAddressModalOpen: false,
    insurerContacts: [],
    isInsContactsModalOpen: false,
    isInsCodeModalOpen: false,
    insurerCodes: [],
    defaultInsurerList: [],
    logoList: {},
    // --- SSO ---
    ssoStatus: "loading", // 'loading' | 'not_configured' | 'pending' | 'active' | 'error'
    ssoConnections: [],    // raw connections array from backend
    pendingConnection: null, // convenience ref for PENDING connection object
    isInitiatingSSO: false,
    ssoError: null,
    isSavingIdpDetails: false,
    // Edit/Delete SAML connection state
    isEditingSamlConnection: false,
    isDeletingSamlConnection: false,
    samlEditError: null,
    samlDeleteError: null,

    orgSettings: null,           // holds { allowed_domains: [], ... }
    isLoadingOrgSettings: false,
    isUpdatingOrgSettings: false,
    orgSettingsError: null,
    stytchOrg: {
      settings: null,
      isLoading: false,
      isUpdating: false,
      error: null,
      status: {
        hasOrganization: false,
        organizationId: null,
        organizationName: null
      },
      members: [],
      setupResults: null
    }

  },
  reducers: {
    stytchOrgSettingsChecking: (brokerage) => {
      brokerage.stytchOrg.isLoading = true;
      brokerage.stytchOrg.error = null;
    },
    stytchOrgSettingsChecked: (brokerage, action) => {
      brokerage.stytchOrg.isLoading = false;
      brokerage.stytchOrg.error = null; // Clear any previous errors
      // Extract data from backend response: { success: true, data: {...} }
      if (action.payload && (action.payload.data || action.payload.success !== false)) {
        brokerage.stytchOrg.settings = action.payload.data || action.payload;
      }
    },
    stytchOrgSettingsCheckFailed: (brokerage, action) => {
      brokerage.stytchOrg.isLoading = false;
      // Handle different error response formats
      const errorPayload = action.payload;
      if (errorPayload?.error) {
        brokerage.stytchOrg.error = errorPayload.error;
        // Try to parse details for more specific error messages
        if (errorPayload.details) {
          try {
            const details = typeof errorPayload.details === 'string' ? JSON.parse(errorPayload.details) : errorPayload.details;
            brokerage.stytchOrg.error = details.error_message || errorPayload.error;
          } catch (e) {
            // Keep the original error if parsing fails
          }
        }
      } else {
        brokerage.stytchOrg.error = errorPayload || 'Failed to load Stytch organization settings';
      }
    },
  
    stytchOrgSettingsUpdating: (brokerage) => {
      brokerage.stytchOrg.isUpdating = true;
      brokerage.stytchOrg.error = null;
    },
    stytchOrgSettingsUpdated: (brokerage, action) => {
      brokerage.stytchOrg.isUpdating = false;
      brokerage.stytchOrg.error = null; // Clear any previous errors
      // Extract data from backend response: { success: true, data: {...} }
      if (action.payload && (action.payload.data || action.payload.success !== false)) {
        const orgData = action.payload.data || action.payload;
        brokerage.stytchOrg.settings = orgData;
        // Also update status if this was a creation
        if (orgData?.organization_id) {
          brokerage.stytchOrg.status = {
            hasOrganization: true,
            organizationId: orgData.organization_id,
            organizationName: orgData.organization_name
          };
        }
      }
    },
    stytchOrgSettingsUpdateFailed: (brokerage, action) => {
      brokerage.stytchOrg.isUpdating = false;
      // Handle different error response formats
      const errorPayload = action.payload;
      if (errorPayload?.error) {
        brokerage.stytchOrg.error = errorPayload.error;
        // Try to parse details for more specific error messages
        if (errorPayload.details) {
          try {
            const details = typeof errorPayload.details === 'string' ? JSON.parse(errorPayload.details) : errorPayload.details;
            brokerage.stytchOrg.error = details.error_message || errorPayload.error;
          } catch (e) {
            // Keep the original error if parsing fails
          }
        }
      } else {
        brokerage.stytchOrg.error = errorPayload || 'Failed to update Stytch organization settings';
      }
    },
  
    stytchOrgCreating: (brokerage) => {
      brokerage.stytchOrg.isUpdating = true;
      brokerage.stytchOrg.error = null;
    },
    stytchOrgCreated: (brokerage, action) => {
      brokerage.stytchOrg.isUpdating = false;
      // Extract data from backend response: { success: true, data: {...} }
      const orgData = action.payload.data || action.payload;
      brokerage.stytchOrg.settings = orgData;
      brokerage.stytchOrg.status = {
        hasOrganization: true,
        organizationId: orgData.organization_id,
        organizationName: orgData.organization_name
      };
    },
    stytchOrgCreateFailed: (brokerage, action) => {
      brokerage.stytchOrg.isUpdating = false;
      // Handle different error response formats
      const errorPayload = action.payload;
      if (errorPayload?.error) {
        brokerage.stytchOrg.error = errorPayload.error;
        // Try to parse details for more specific error messages
        if (errorPayload.details) {
          try {
            const details = typeof errorPayload.details === 'string' ? JSON.parse(errorPayload.details) : errorPayload.details;
            brokerage.stytchOrg.error = details.error_message || errorPayload.error;
          } catch (e) {
            // Keep the original error if parsing fails
          }
        }
      } else {
        brokerage.stytchOrg.error = errorPayload || 'Failed to create Stytch organization';
      }
    },
  
    stytchOrgStatusChecked: (brokerage, action) => {
      brokerage.stytchOrg.error = null; // Clear any previous errors
      // Extract from backend response: { success: true, has_organization: boolean, organization_id: string, organization_name: string }
      if (action.payload && action.payload.success !== false) {
        const responseData = action.payload.data || action.payload;
        brokerage.stytchOrg.status = {
          hasOrganization: responseData.has_organization || false,
          organizationId: responseData.organization_id || null,
          organizationName: responseData.organization_name || null
        };
      }
    },
  
    stytchOrgMembersChecked: (brokerage, action) => {
      brokerage.stytchOrg.error = null; // Clear any previous errors
      // Extract from backend response: { success: true, members: [...], total_count: number }
      if (action.payload && action.payload.success !== false) {
        brokerage.stytchOrg.members = action.payload.members || [];
      }
    },
    
    stytchOrgMemberInviting: (brokerage) => {
      brokerage.stytchOrg.isUpdating = true;
      brokerage.stytchOrg.error = null;
    },
    stytchOrgMemberInvited: (brokerage, action) => {
      brokerage.stytchOrg.isUpdating = false;
      // Add the new member to the list if it's not already there
      if (action.payload && !brokerage.stytchOrg.members.find(m => m.member_id === action.payload.member_id)) {
        brokerage.stytchOrg.members.push(action.payload);
      }
    },
    stytchOrgMemberInviteFailed: (brokerage, action) => {
      brokerage.stytchOrg.isUpdating = false;
      // Handle different error response formats
      const errorPayload = action.payload;
      if (errorPayload?.error) {
        brokerage.stytchOrg.error = errorPayload.error;
        // Try to parse details for more specific error messages
        if (errorPayload.details) {
          try {
            const details = typeof errorPayload.details === 'string' ? JSON.parse(errorPayload.details) : errorPayload.details;
            brokerage.stytchOrg.error = details.error_message || errorPayload.error;
          } catch (e) {
            // Keep the original error if parsing fails
          }
        }
      } else {
        brokerage.stytchOrg.error = errorPayload || 'Failed to invite member';
      }
    },
  
    stytchOrgDeleted: (brokerage) => {
      brokerage.stytchOrg.isUpdating = false;
      brokerage.stytchOrg.settings = null;
      brokerage.stytchOrg.status = {
        hasOrganization: false,
        organizationId: null,
        organizationName: null
      };
      brokerage.stytchOrg.members = [];
    },
    // Auto-setup reducers
    stytchAutoSetupStarted: (brokerage) => {
      brokerage.stytchOrg.isUpdating = true;
      brokerage.stytchOrg.error = null;
    },
    stytchAutoSetupCompleted: (brokerage, action) => {
      brokerage.stytchOrg.isUpdating = false;
      brokerage.stytchOrg.error = null;
      // Store the setup results for display
      brokerage.stytchOrg.setupResults = action.payload.results || action.payload;
    },
    stytchAutoSetupFailed: (brokerage, action) => {
      brokerage.stytchOrg.isUpdating = false;
      // Handle different error response formats
      const errorPayload = action.payload;
      if (errorPayload?.error) {
        brokerage.stytchOrg.error = errorPayload.error;
        if (errorPayload.details) {
          try {
            const details = typeof errorPayload.details === 'string' ? JSON.parse(errorPayload.details) : errorPayload.details;
            brokerage.stytchOrg.error = details.error_message || errorPayload.error;
          } catch (e) {
            // Keep the original error if parsing fails
          }
        }
      } else {
        brokerage.stytchOrg.error = errorPayload || 'Failed to run auto-setup';
      }
    },
    //stych end
    informationRetrieving: (brokerage) => {
      brokerage.information = [];
    },
    informationRetrieved: (brokerage, action) => {
      brokerage.information = action.payload ? action.payload : [];
    },
    informationRetrievalFailed: (brokerage) => {
      brokerage.information = [];
    },

    detailsUpdating: (brokerage) => {
      brokerage.isUpdating = true;
    },
    detailsUpdated: (brokerage) => {
      brokerage.isUpdating = false;
    },
    detailsUpdateFailed: (brokerage, action) => {
      brokerage.isUpdating = false;
      brokerage.updateErrors = action.payload ? action.payload : "Error";
    },

    insurerContactsRetrieving: (brokerage) => {
      brokerage.insurerContacts = [];
    },
    insurerContactsRetrieved: (brokerage, action) => {
      brokerage.insurerContacts = action.payload ? action.payload : [];
    },
    insurerContactsRetrievalFailed: (brokerage) => {
      brokerage.insurerContacts = [];
    },

    insurerCodesRetrieving: (brokerage) => {
      brokerage.insurerCodes = [];
    },
    insurerCodesRetrieved: (brokerage, action) => {
      brokerage.insurerCodes = action.payload ? action.payload : [];
    },
    insurerCodesRetrievalFailed: (brokerage) => {
      brokerage.insurerCodes = [];
    },

    InsContactsModalToggled: (brokerage, action) => {
      brokerage.isInsContactsModalOpen = action.payload.status;
    },
    InsCodeModalToggled: (brokerage, action) => {
      brokerage.isInsCodeModalOpen = action.payload.status;
    },
    AddressModalToggled: (brokerage, action) => {
      brokerage.isAddressModalOpen = action.payload.status;
    },
    defaultInsurerListRetrieved: (brokerage, action) => {
      brokerage.defaultInsurerList = action.payload ? action.payload : [];
    },
    defaultInsurerListFailed: (brokerage) => {
      brokerage.defaultInsurerList = [];
    },
    insurersLogosReceived: (brokerage, action) => {
      brokerage.logoList = action.payload;
    },

    // ------------------- SSO reducers -------------------
    ssoStatusChecked: (brokerage, action) => {
      const { status, pendingConnection, activeConnection, connections = [] } = action.payload;
      console.log('SSO Status Payload:', action.payload);
      
      brokerage.ssoStatus = status;
      brokerage.ssoConnections = connections;
      
      // Handle different response structures correctly
      if (status === 'pending' && pendingConnection) {
        brokerage.pendingConnection = pendingConnection;
      } else if (status === 'active' && activeConnection) {
        brokerage.pendingConnection = null;
      } else {
        brokerage.pendingConnection = null;
      }
    },
  
    // ssoInitiated: (brokerage, action) => {
    //   brokerage.isInitiatingSSO = false;
    //   brokerage.ssoStatus = "pending";
    //   brokerage.pendingConnection = action.payload;
    // },
  
    samlIdpSaved: (brokerage, action) => {
      brokerage.isSavingIdpDetails = false;
      brokerage.ssoStatus = action.payload.status || 'active';
      brokerage.pendingConnection = null;
      brokerage.ssoConnections = action.payload.connections || brokerage.ssoConnections;
    },
    ssoStatusChecking: (brokerage) => {
      brokerage.ssoStatus = "loading";
      brokerage.ssoError = null;
    },
    // Expect payload like { status, connections? }
    // ssoStatusChecked: (brokerage, action) => {
    //   const { status, connections = [], pendingConnection = null } = action.payload;
    //   console.log('emparoe',action.payload)
    //   brokerage.ssoStatus = status;
    //   brokerage.ssoConnections = connections;
    //   brokerage.pendingConnection = action.payload.setupData[0];
    // },
    ssoStatusCheckFailed: (brokerage, action) => {
      brokerage.ssoStatus = "error";
      brokerage.ssoError = action.payload?.error || action.payload || "Unknown error";
    },

    ssoInitiating: (brokerage) => {
      brokerage.isInitiatingSSO = true;
      brokerage.ssoError = null;
    },
    ssoInitiationFailed: (brokerage, action) => {
      brokerage.isInitiatingSSO = false;
      brokerage.ssoError = action.payload || "Failed to initiate SSO.";
    },
    

    samlIdpSaving: (brokerage) => {
      brokerage.isSavingIdpDetails = true;
      brokerage.ssoError = null;
    },
    // samlIdpSaved: (brokerage, action) => {
    //   brokerage.isSavingIdpDetails = false;
    //   brokerage.ssoStatus = action.payload.status; // likely 'active'
    //   brokerage.pendingConnection = null;
    //   brokerage.ssoConnections = action.payload.connections || brokerage.ssoConnections;
    // },
    samlIdpSaveFailed: (brokerage, action) => {
      brokerage.isSavingIdpDetails = false;
      brokerage.ssoError = action.payload || "Failed to save IdP details.";
    },

    // Metadata URL processing reducers
    samlMetadataUrlSaving: (brokerage) => {
      brokerage.isSavingIdpDetails = true;
      brokerage.ssoError = null;
    },
    samlMetadataUrlSaved: (brokerage, action) => {
      brokerage.isSavingIdpDetails = false;
      brokerage.ssoStatus = action.payload.status || 'active';
      brokerage.ssoConnections = action.payload.connections || brokerage.ssoConnections;
      
      // Only clear pendingConnection if status is active, otherwise keep it for pending state
      if (action.payload.status === 'active') {
        brokerage.pendingConnection = null;
      } else {
        // For pending status, use the returned connection as pending connection
        brokerage.pendingConnection = action.payload.connection;
      }
    },
    samlMetadataUrlSaveFailed: (brokerage, action) => {
      brokerage.isSavingIdpDetails = false;
      brokerage.ssoError = action.payload || "Failed to process metadata URL.";
    },
    orgSettingsChecking: (brokerage) => {
      brokerage.isLoadingOrgSettings = true;
      brokerage.orgSettingsError = null;
    },
    orgSettingsChecked: (brokerage, action) => {
      brokerage.isLoadingOrgSettings = false;
      brokerage.orgSettings = action.payload;
    },
    orgSettingsCheckFailed: (brokerage, action) => {
      brokerage.isLoadingOrgSettings = false;
      brokerage.orgSettingsError = action.payload || 'Failed to load organization settings';
    },
    orgSettingsUpdating: (brokerage) => {
      brokerage.isUpdatingOrgSettings = true;
      brokerage.orgSettingsError = null;
    },
    orgSettingsUpdated: (brokerage, action) => {
      brokerage.isUpdatingOrgSettings = false;
      brokerage.orgSettings = action.payload;
    },
    orgSettingsUpdateFailed: (brokerage, action) => {
      brokerage.isUpdatingOrgSettings = false;
      brokerage.orgSettingsError = action.payload || 'Failed to update organization settings';
    },
    ssoInitiated: (brokerage, action) => {
      console.log('SSO Initiated - Full Payload:', action.payload);
      brokerage.isInitiatingSSO = false;
      brokerage.ssoStatus = "pending";
      
      // Handle your backend's response structure - it returns { setupData: {...}, status: "pending" }
      const responseData = action.payload;
      if (responseData.setupData) {
        brokerage.pendingConnection = responseData.setupData;
      } else if (responseData.pendingConnection) {
        brokerage.pendingConnection = responseData.pendingConnection;
      } else {
        // Fallback: if the payload itself looks like connection data
        brokerage.pendingConnection = responseData;
      }
    },

    // Edit SAML Connection reducers
    samlConnectionEditing: (brokerage) => {
      brokerage.isEditingSamlConnection = true;
      brokerage.samlEditError = null;
    },
    samlConnectionEdited: (brokerage, action) => {
      brokerage.isEditingSamlConnection = false;
      brokerage.samlEditError = null;
      brokerage.ssoStatus = action.payload.status || 'active';
      brokerage.ssoConnections = action.payload.connections || brokerage.ssoConnections;
      
      // Clear pending connection if status is active
      if (action.payload.status === 'active') {
        brokerage.pendingConnection = null;
      }
    },
    samlConnectionEditFailed: (brokerage, action) => {
      brokerage.isEditingSamlConnection = false;
      brokerage.samlEditError = action.payload || "Failed to edit SAML connection.";
    },

    // Delete SAML Connection reducers  
    samlConnectionDeleting: (brokerage) => {
      brokerage.isDeletingSamlConnection = true;
      brokerage.samlDeleteError = null;
    },
    samlConnectionDeleted: (brokerage, action) => {
      brokerage.isDeletingSamlConnection = false;
      brokerage.samlDeleteError = null;
      brokerage.ssoStatus = action.payload.status || 'not_configured';
      brokerage.ssoConnections = action.payload.connections || [];
      brokerage.pendingConnection = null; // Always clear pending connection when deleting
    },
    samlConnectionDeleteFailed: (brokerage, action) => {
      brokerage.isDeletingSamlConnection = false;
      brokerage.samlDeleteError = action.payload || "Failed to delete SAML connection.";
    },
  },
});

export const {
  informationRetrieving,
  informationRetrieved,
  informationRetrievalFailed,
  detailsUpdating,
  detailsUpdated,
  detailsUpdateFailed,
  insurerContactsRetrieving,
  insurerContactsRetrieved,
  insurerContactsRetrievalFailed,
  insurerCodesRetrieving,
  insurerCodesRetrieved,
  insurerCodesRetrievalFailed,
  AddressModalToggled,
  InsContactsModalToggled,
  InsCodeModalToggled,
  defaultInsurerListRetrieved,
  defaultInsurerListFailed,
  insurersLogosReceived,
  ssoStatusChecking,
  ssoStatusChecked,
  ssoStatusCheckFailed,
  ssoInitiating,
  ssoInitiationFailed,
  ssoInitiated,
  samlIdpSaving,
  samlIdpSaved,
  samlIdpSaveFailed,
  samlMetadataUrlSaving,
  samlMetadataUrlSaved,
  samlMetadataUrlSaveFailed,
  orgSettingsChecking,
  orgSettingsChecked,
  orgSettingsCheckFailed,
  orgSettingsUpdating,
  orgSettingsUpdated,
  orgSettingsUpdateFailed,
  // Stytch organization actions
  stytchOrgSettingsChecking,
  stytchOrgSettingsChecked,
  stytchOrgSettingsCheckFailed,
  stytchOrgSettingsUpdating,
  stytchOrgSettingsUpdated,
  stytchOrgSettingsUpdateFailed,
  stytchOrgCreating,
  stytchOrgCreated,
  stytchOrgCreateFailed,
  stytchOrgStatusChecked,
  stytchOrgMembersChecked,
  stytchOrgMemberInviting,
  stytchOrgMemberInvited,
  stytchOrgMemberInviteFailed,
  stytchOrgDeleted,
  stytchAutoSetupStarted,
  stytchAutoSetupCompleted,
  stytchAutoSetupFailed,
  // SAML Connection Edit/Delete actions
  samlConnectionEditing,
  samlConnectionEdited,
  samlConnectionEditFailed,
  samlConnectionDeleting,
  samlConnectionDeleted,
  samlConnectionDeleteFailed
} = slice.actions;

export default slice.reducer;

// ---------------------- API CALLS (existing) ----------------------

export const getStytchOrgSettings = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/stytch/organization/settings",
    method: "get",
    onStart: "brokerage/stytchOrgSettingsChecking",
    onSuccess: "brokerage/stytchOrgSettingsChecked",
    onError: "brokerage/stytchOrgSettingsCheckFailed",
  });
 
export const updateStytchOrgSettings = (settings) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/stytch/organization/settings",
    method: "put",
    data: settings,
    onStart: "brokerage/stytchOrgSettingsUpdating",
    onSuccess: "brokerage/stytchOrgSettingsUpdated",
    onError: "brokerage/stytchOrgSettingsUpdateFailed",
  });

export const createStytchOrganization = (orgData) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/stytch/organization/create",
    method: "post",
    data: orgData,
    onStart: "brokerage/stytchOrgCreating",
    onSuccess: "brokerage/stytchOrgCreated",
    onError: "brokerage/stytchOrgCreateFailed",
  });

export const getStytchOrgStatus = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/stytch/organization/status",
    method: "get",
    onSuccess: "brokerage/stytchOrgStatusChecked",
    onError: "brokerage/stytchOrgSettingsCheckFailed",
  });

export const getStytchOrgMembers = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/stytch/organization/members",
    method: "get",
    onSuccess: "brokerage/stytchOrgMembersChecked",
    onError: "brokerage/stytchOrgSettingsCheckFailed",
  });

export const inviteStytchOrgMember = (inviteData) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/stytch/organization/invite",
    method: "post",
    data: inviteData,
    onStart: "brokerage/stytchOrgMemberInviting",
    onSuccess: "brokerage/stytchOrgMemberInvited",
    onError: "brokerage/stytchOrgMemberInviteFailed",
  });

export const deleteStytchOrganization = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/stytch/organization",
    method: "delete",
    onStart: "brokerage/stytchOrgSettingsUpdating",
    onSuccess: "brokerage/stytchOrgDeleted",
    onError: "brokerage/stytchOrgSettingsUpdateFailed",
  });

export const runStytchAutoSetup = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/stytch/auto-setup",
    method: "post",
    onStart: "brokerage/stytchAutoSetupStarted",
    onSuccess: "brokerage/stytchAutoSetupCompleted",
    onError: "brokerage/stytchAutoSetupFailed",
  });


export const getDetails = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/brokeragedetails",
    method: "get",
    onStart: informationRetrieving.type,
    onSuccess: informationRetrieved.type,
    onError: informationRetrievalFailed.type,
  });

export const getDetailsAdmin = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/brokeragedetails/admin?code=" + data,
    method: "get",
    onStart: informationRetrieving.type,
    onSuccess: informationRetrieved.type,
    onError: informationRetrievalFailed.type,
  });

export const getInsContacts = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/insurercontacts",
    method: "post",
    data,
    onStart: insurerContactsRetrieving.type,
    onSuccess: insurerContactsRetrieved.type,
    onError: insurerContactsRetrievalFailed.type,
  });

export const getDefaultInsurersList = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/insurercontacts/getAllInsurers?code=" + data,
    method: "get",
    onSuccess: defaultInsurerListRetrieved.type,
    onError: defaultInsurerListFailed.type,
  });

  export const getOrgSettings = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/brokerages/org",
    method: "get",
    onStart: orgSettingsChecking.type,
    onSuccess: orgSettingsChecked.type,
    onError: orgSettingsCheckFailed.type,
  });

export const updateOrgSettings = (settings) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/brokerages/org",
    method: "put",
    data: settings,
    onStart: orgSettingsUpdating.type,
    onSuccess: orgSettingsUpdated.type,
    onError: orgSettingsUpdateFailed.type,
  });


export const getOrgSettingsSelector = createSelector(
  state => state.entities.brokerage,
  brokerage => brokerage.orgSettings
);
export const getIsLoadingOrgSettingsSelector = createSelector(
  state => state.entities.brokerage,
  brokerage => brokerage.isLoadingOrgSettings
);
export const getIsUpdatingOrgSettingsSelector = createSelector(
  state => state.entities.brokerage,
  brokerage => brokerage.isUpdatingOrgSettings
);
export const getOrgSettingsErrorSelector = createSelector(
  state => state.entities.brokerage,
  brokerage => brokerage.orgSettingsError
);

export const toggleInsContactsModal = createAction(
  InsContactsModalToggled.type,
  function toggle(toggleFlag) {
    return {
      payload: {
        status: toggleFlag,
      },
    };
  }
);

export const getInsContactsStatus = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.isInsContactsModalOpen
);

export const toggleInsurerCodesModal = createAction(
  InsCodeModalToggled.type,
  function toggle(toggleFlag) {
    return {
      payload: {
        status: toggleFlag,
      },
    };
  }
);

export const getInsCodeStatus = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.isInsCodeModalOpen
);

export const toggleAddressModal = createAction(
  AddressModalToggled.type,
  function toggle(toggleFlag) {
    return {
      payload: {
        status: toggleFlag,
      },
    };
  }
);

export const getAddressStatus = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.isAddressModalOpen
);

export const updateDetails = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/brokeragedetails/update",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

export const addBranch = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/brokeragedetails/addbranch",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

export const deleteBranch = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/brokeragedetails/removebranch",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

export const addInsContact = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/insurercontacts/addcontact",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

export const getInsurerLogos = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/insurercontacts/getInsurerLogos",
    method: "get",
    onSuccess: insurersLogosReceived.type,
  });

export const addInsCodes = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/brokeragedetails/addinsurercodes",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

export const getInsCodes = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/brokeragedetails/getinsurercodes",
    method: "post",
    data,
    onStart: insurerCodesRetrieving.type,
    onSuccess: insurerCodesRetrieved.type,
    onError: insurerCodesRetrievalFailed.type,
  });

export const addBrokerageInsurer = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/insurercontacts/addbrokerageinsurer",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

export const deleteInsContact = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/insurercontacts/removecontact",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

export const updateIsActiveStatus = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/insurercontacts/setIsActiveStatus",
    method: "post",
    data,
  });

export const updateBMS = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/brokeragedetails/setBMS",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

export const updateAllIsActiveStatus = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/insurercontacts/setAllIsActiveStatus",
    method: "post",
    data,
  });

export const updateUserActiveStatus = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/insurercontacts/setUserActiveStatus",
    method: "post",
    data,
  });

export const updateInsContactDefault = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "api/insurercontacts/updatedefault",
    method: "post",
    data,
    onStart: detailsUpdating.type,
    onSuccess: detailsUpdated.type,
    onError: detailsUpdateFailed.type,
  });

// ---------------------- NEW SSO THUNKS ----------------------

// GET current SSO status + connections
export const getSSOStatus = () =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/brokerages/status",
    method: "get",
    onStart: ssoStatusChecking.type,
    onSuccess: ssoStatusChecked.type,
    onError: ssoStatusCheckFailed.type,
  });

// Create connection (or reuse pending) and redirect if backend returns setup_url
// export const initiateSSOOnboardingAndRedirect = () => async (dispatch) => {
//   dispatch({ type: ssoInitiating.type });
//   try {
//     const resp = await dispatch(
//       apiCallBegan({
//         serviceUrl: process.env.REACT_APP_BASICSERVICE,
//         url: "/api/brokerages/sso/saml/connection", // your create-if-missing endpoint
//         method: "post",
//       })
//     );

//     const data = resp?.data;
//     if (!data) throw new Error("Empty response");

//     if (data.setup_url) {
//       // immediate redirect
//       window.location.href = data.setup_url;
//       return;
//     }

//     // no redirect -> store pending connection locally
//     dispatch({ type: ssoInitiated.type, payload: data.pendingConnection || data });
//   } catch (err) {
//     dispatch({ type: ssoInitiationFailed.type, payload: err.message });
//   }
// }; 

// Submit IdP details (user pastes cert/urls into FE form)
// export const submitSamlIdpDetails = ({
//   connection_id,
//   idp_sso_url,
//   idp_entity_id,
//   x509_certificate,
//   attribute_mapping,
// }) => async (dispatch) => {
//   dispatch({ type: samlIdpSaving.type });
//   try {
//     const resp = await dispatch(
//       apiCallBegan({
//         serviceUrl: process.env.REACT_APP_BASICSERVICE,
//         url: `/api/brokerages/sso/saml/connection/${connection_id}`,
//         method: "put",
//         data: {
//           idp_sso_url,
//           idp_entity_id,
//           x509_certificate,
//           attribute_mapping,
//         },
//       })
//     );

//     dispatch({ type: samlIdpSaved.type, payload: resp.data });
//   } catch (err) {
//     dispatch({ type: samlIdpSaveFailed.type, payload: err.message });
//   }
// };

export const initiateSSOOnboardingAndRedirect = () => 
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/brokerages/saml/connection",
    method: "post",
    onStart: ssoInitiating.type,
    onSuccess: ssoInitiated.type,        // ← Will handle response
    onError: ssoInitiationFailed.type,
  });
  export const submitSamlIdpDetails = ({
    connection_id,
    idp_sso_url,
    idp_entity_id,
    x509_certificate,
    attribute_mapping,
    idp_slo_url,
  }) => {
    // Ensure attribute_mapping has required fields
    const validatedAttributeMapping = {
      email: attribute_mapping.email || 'email',
      first_name: attribute_mapping.first_name || 'firstName',
      last_name: attribute_mapping.last_name || 'lastName',
      ...attribute_mapping // Include any additional mappings
    };
  
    return apiCallBegan({
      serviceUrl: process.env.REACT_APP_BASICSERVICE,
      url: `/api/brokerages/saml/connection/${connection_id}`,
      method: "put",
      data: {
        idp_sso_url,
        idp_entity_id,
        x509_certificate,
        attribute_mapping: validatedAttributeMapping,
        ...(idp_slo_url && { idp_slo_url }),
      },
      onStart: samlIdpSaving.type,
      onSuccess: samlIdpSaved.type,    // ← This will handle the response data
      onError: samlIdpSaveFailed.type,
    });
  };

  // Submit IdP metadata URL (user pastes metadata URL from IdP)
  export const submitSamlMetadataUrl = ({
    connection_id,
    metadata_url,
  }) => {
    return apiCallBegan({
      serviceUrl: process.env.REACT_APP_BASICSERVICE,
      url: `/api/brokerages/saml/connection/${connection_id}/metadata-url`,
      method: "put",
      data: { metadata_url },
      onStart: samlMetadataUrlSaving.type,
      onSuccess: samlMetadataUrlSaved.type,
      onError: samlMetadataUrlSaveFailed.type,
    });
  };

// export const submitSamlIdpDetails = ({
//   connection_id,
//   idp_sso_url,
//   idp_entity_id,
//   x509_certificate,
//   attribute_mapping,
//   idp_slo_url, // Added optional SLO URL
// }) => async (dispatch) => {
//   dispatch({ type: samlIdpSaving.type });
//   try {
//     const resp = await dispatch(
//       apiCallBegan({
//         serviceUrl: process.env.REACT_APP_BASICSERVICE,
//         url: `/api/brokerages/saml/connection/${connection_id}`, // Updated endpoint
//         method: "put",
//         data: {
//           idp_sso_url,
//           idp_entity_id,
//           x509_certificate,
//           attribute_mapping,
//           ...(idp_slo_url && { idp_slo_url }), // Include SLO URL if provided
//         },
//       })
//     );

//     dispatch({ type: samlIdpSaved.type, payload: resp.data });
//   } catch (err) {
//     dispatch({ 
//       type: samlIdpSaveFailed.type, 
//       payload: err.response?.data?.error || err.message 
//     });
//   }
// };

// Edit SAML connection
export const editSamlConnection = ({
  connection_id,
  idp_sso_url,
  idp_entity_id,
  x509_certificate,
  attribute_mapping = { email: 'email', first_name: 'firstName', last_name: 'lastName' },
  idp_slo_url,
}) => {
  // Ensure attribute_mapping has required fields
  const validatedAttributeMapping = {
    email: attribute_mapping.email || 'email',
    first_name: attribute_mapping.first_name || 'firstName',
    last_name: attribute_mapping.last_name || 'lastName',
    ...attribute_mapping // Include any additional mappings
  };

  return apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: `/api/brokerages/saml/connection/${connection_id}/edit`,
    method: "put",
    data: {
      idp_sso_url,
      idp_entity_id,
      x509_certificate,
      attribute_mapping: validatedAttributeMapping,
      ...(idp_slo_url && { idp_slo_url }),
    },
    onStart: samlConnectionEditing.type,
    onSuccess: samlConnectionEdited.type,
    onError: samlConnectionEditFailed.type,
  });
};

// Delete SAML connection
export const deleteSamlConnection = (connection_id) => {
  return apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: `/api/brokerages/saml/connection/${connection_id}`,
    method: "delete",
    onStart: samlConnectionDeleting.type,
    onSuccess: samlConnectionDeleted.type,
    onError: samlConnectionDeleteFailed.type,
  });
};

// ---------------------- SELECTORS ----------------------
export const getSSOStatusSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.ssoStatus
);

export const getIsInitiatingSSOSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.isInitiatingSSO
);

export const getSSOErrorSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.ssoError
);

export const getPendingConnectionSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.pendingConnection
);

export const getIsSavingIdpSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.isSavingIdpDetails
);

export const getSSOConnectionsSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.ssoConnections
);

export const getActiveConnectionSelector = createSelector(
  getSSOConnectionsSelector,
  (connections) => connections?.find(conn => conn.status === 'active') || null
);

// Edit/Delete SAML Connection Selectors
export const getIsEditingSamlConnectionSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.isEditingSamlConnection
);

export const getIsDeletingSamlConnectionSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.isDeletingSamlConnection
);

export const getSamlEditErrorSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.samlEditError
);

export const getSamlDeleteErrorSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.samlDeleteError
);

export const getBrokerageInformation = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.information
);

export const getInsContactsInformation = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.insurerContacts
);

export const getDefaultInsurersListSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.defaultInsurerList
);

export const getInsurerCodesSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.insurerCodes
);

export const getIsUpdating = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.isUpdating
);

export const getUpdateErrors = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.updateErrors
);

export const getLogoList = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.logoList
);

// ---------------------- Stytch Organization Selectors ----------------------
export const getStytchOrgSettingsSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.stytchOrg.settings
);

export const getIsLoadingStytchOrgSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.stytchOrg.isLoading
);

export const getStytchOrgErrorSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.stytchOrg.error
);

export const getIsUpdatingStytchOrgSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.stytchOrg.isUpdating
);

export const getStytchOrgStatusSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.stytchOrg.status
);

export const getStytchOrgMembersSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.stytchOrg.members
);

export const getStytchAutoSetupResultsSelector = createSelector(
  (state) => state.entities.brokerage,
  (brokerage) => brokerage.stytchOrg.setupResults
);